import { useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../../components/SEO";

// All images sourced from fostercareanthemproject.com
const GALLERY = [
  "https://fostercareanthemproject.com/gallery/awareness/if-we-dont-1.jpg",
  "https://fostercareanthemproject.com/gallery/awareness/if-we-dont-2.jpg",
  "https://fostercareanthemproject.com/gallery/awareness/if-we-dont-3.jpg",
  "https://fostercareanthemproject.com/gallery/awareness/if-we-dont-4.jpg",
  "https://fostercareanthemproject.com/gallery/philly-foster-kid/01.jpg",
  "https://fostercareanthemproject.com/gallery/philly-foster-kid/04.jpg",
  "https://fostercareanthemproject.com/gallery/philly-foster-kid/07.jpg",
  "https://fostercareanthemproject.com/gallery/philly-foster-kid/11.jpg",
  "https://fostercareanthemproject.com/gallery/philly-foster-kid/14.jpg",
  "https://fostercareanthemproject.com/gallery/philly-foster-kid/17.jpg",
  "https://fostercareanthemproject.com/gallery/image-large/01.jpg",
  "https://fostercareanthemproject.com/gallery/image-large/03.jpg",
  "https://fostercareanthemproject.com/gallery/songs/bring-it-on.jpg",
  "https://fostercareanthemproject.com/gallery/songs/how-long.jpg",
  "https://fostercareanthemproject.com/gallery/songs/lost-then-found.jpg",
  "https://fostercareanthemproject.com/gallery/songs/tremendous-day.jpg",
  "https://fostercareanthemproject.com/gallery/songs/holy-ghost.jpg",
  "https://fostercareanthemproject.com/gallery/songs/the-next-time-i-see-you.jpg",
];

const STORIES = [
  { slug: "bring-it-on", title: "Bring It On", note: "Every demonic attack or whatever is thrown at us, we have the strength of FATHER GOD, JESUS & THE HOLY SPIRIT behind us. Bring it on." },
  { slug: "philly-foster-kid", title: "Philly Foster Kid", note: "Twenty-two hundred of my fellow homies, waiting for their forever home. Not invisible. Not forgotten." },
  { slug: "holy-ghost", title: "Holy Ghost", note: "For every foster heart — make a home, ignite their fire." },
  { slug: "lost-then-found", title: "Lost Then Found", note: "Father God rebuilt what the devil tore down." },
];

export default function Stories() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div data-testid="anthem-stories-page">
      <SEO
        path="/anthem/stories"
        title="Stories | Foster Care Anthem · Faces of the Mission"
        description="Editorial galleries, song excerpts, and the trafficking-awareness videos behind Foster Care Anthem. If we don't do it, who will? Every frame honors a real child still living inside the foster system today."
      />
      <section className="border-b border-an-border">
        <div className="container-xl pt-24 pb-16">
          <div className="overline text-an-gold mb-5">Stories · Faces of the Mission</div>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.9]">If we don't do it, <br/><span className="italic">who will?</span></h1>
          <p className="mt-8 text-an-muted text-lg max-w-3xl leading-relaxed">
            Every frame, every line, every verse is rendered to honor a real child still living inside the foster system today.
          </p>
        </div>
      </section>

      {/* Editorial gallery */}
      <section className="border-b border-an-border">
        <div className="container-xl py-20">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {GALLERY.map((img, i) => {
              const span = i % 7 === 0 ? "md:col-span-2 md:row-span-2 aspect-[4/5]" : "aspect-square";
              return (
                <div key={i} className={`${span} overflow-hidden border border-an-border bg-an-surface`}>
                  <img src={img} alt="" loading="lazy" className="w-full h-full object-cover grayscale-[0.15] hover:grayscale-0 hover:scale-105 transition-all duration-700" />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* We're Coming For You — Trafficking Awareness Feature */}
      <section className="border-b border-an-border bg-an-surface/40" data-testid="stories-wcfy-feature">
        <div className="container-xl py-24">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-12">
            <div className="md:col-span-8">
              <div className="overline text-an-gold mb-4">Trafficking Awareness Series · First Release</div>
              <h2 className="font-display text-4xl md:text-6xl leading-[0.95]">
                We're Coming <br/><span className="italic">For You.</span>
              </h2>
              <p className="mt-6 text-an-muted leading-relaxed max-w-2xl">
                A direct address to children trapped in human trafficking — and a vow to those fighting to bring them home. The first release in an ongoing trafficking-awareness series. Two videos. One single. Zero hesitation.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link to="/anthem/were-coming-for-you" className="btn-outline" data-testid="stories-wcfy-cta">
                View All Editions →
              </Link>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-an-border bg-an-bg" data-testid="stories-wcfy-video-original">
              <div className="aspect-video overflow-hidden bg-an-bg">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/3b6DNPr5bJI?rel=0"
                  title="We're Coming For You — Original"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="p-5 flex items-baseline justify-between">
                <div>
                  <div className="overline text-an-gold mb-1">/ 01</div>
                  <div className="font-display text-xl">Original Version</div>
                </div>
                <div className="font-mono text-xs text-an-muted">Watch on YouTube</div>
              </div>
            </div>
            <div className="border border-an-border bg-an-bg" data-testid="stories-wcfy-video-rnb">
              <div className="aspect-video overflow-hidden bg-an-bg">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/hBVCoG4Smfo?rel=0"
                  title="We're Coming For You — R&B Version"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="p-5 flex items-baseline justify-between">
                <div>
                  <div className="overline text-an-gold mb-1">/ 02</div>
                  <div className="font-display text-xl">R&B Version</div>
                </div>
                <div className="font-mono text-xs text-an-muted">Watch on YouTube</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story excerpts */}
      <section className="border-b border-an-border">
        <div className="container-xl py-24">
          <h2 className="font-display text-4xl md:text-5xl mb-12">From the notebook.</h2>
          <div className="grid md:grid-cols-2 gap-10">
            {STORIES.map((s) => (
              <Link key={s.slug} to={`/anthem/song/${s.slug}`} className="group border-t border-an-border pt-6 block" data-testid={`story-${s.slug}`}>
                <div className="overline text-an-gold mb-3">Excerpt</div>
                <blockquote className="font-display text-2xl md:text-3xl italic leading-tight mb-6">"{s.note}"</blockquote>
                <div className="flex items-center justify-between">
                  <div className="font-mono text-sm text-an-muted">— {s.title}</div>
                  <span className="overline link-ul text-an-ink">Open Track →</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
